// caiet UC91-11/12/13/15 — the option lists behind the correlated filters.
// The register's facet endpoint answers "which (Registrar, region, locality,
// site) tuples does this session's scope cover": every site in it, reported or
// not and active or not, and every Registrar (UC91-05: "Registrator (R1–R7) …
// Locație (toate)"). The correlation is a projection over the same tuples, and
// an inactive site is offered marked (docs/shape-uc-93-submission-register.md
// §Business day, filter values and history, §2).
//
// It answers that for the roles UC93-01 grants the register. It cannot answer it
// for an Observer, and never could: `scopeFromSession`
// (`apps/bff/src/stock/submissions.controller.ts:77`) refuses them by design.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { Role } from '@simsm/shared-types';
import { apiClient } from '../../app/api-client';
import { listSubmissionFacets, type SubmissionPlace } from '../../features/reports/api';
import { listImspRegistrars, type ImspRegistrar } from '../../features/cross-imsp/api';
import { registrarFacetLabel } from './registrar-label';

export interface ScopeOption {
  value: string;
  label: string;
  // A site the catalogue has inactivated: offered, and marked as such.
  inactive?: boolean;
}

// Which roles UC93-01 grants the submissions register, mirroring
// `scopeFromSession` in the bff. A role outside it is not "a session whose
// answer happens to be empty" — it is a 403 on every load (parity A-32).
export function canReadSubmissionRegister(role: Role | undefined): boolean {
  return role === 'SYSTEM_ADMIN' || role === 'REGISTRAR_ADMIN' || role === 'REGISTRAR_OPERATOR';
}

export function useStockScope(role: Role | undefined): UseQueryResult<SubmissionPlace[]> {
  return useQuery({
    queryKey: ['stock-scope-facets'],
    queryFn: listSubmissionFacets,
    enabled: canReadSubmissionRegister(role),
    staleTime: 300_000,
    retry: false,
  });
}

// caiet UC91-12 — "Administratorul Sistemului și Observatorul pot filtra
// stocurile după Registrator, însă Observatorul poate selecta doar Registratori
// de tip IMSP". That list is the IMSP register, which an Observer may read; the
// reports it appears in are not. Enumerating institutions is not reading their
// shelves, so this is the source the Observer's Registrar filter was missing.
export function useImspRegistrars(enabled: boolean): UseQueryResult<ImspRegistrar[]> {
  return useQuery({
    queryKey: ['uc92-imsp-registrars'],
    queryFn: listImspRegistrars,
    enabled,
    staleTime: 300_000,
    retry: false,
  });
}

export interface StockRegion {
  id: string;
  name: string;
}

// caiet UC91-11 — "Administratorul Sistemului și Observatorul pot filtra stocurile
// după una sau mai multe regiuni". The Observer has no register to derive them
// from (above), so they are the first level of the geography catalogue, which is
// what a region is (CF-35) and what the stock filter matches.
export async function listStockRegions(): Promise<StockRegion[]> {
  const { data } = await apiClient.get<{ regions: StockRegion[] }>('/stock/regions');
  return data.regions ?? [];
}

export function useStockRegions(enabled: boolean): UseQueryResult<StockRegion[]> {
  return useQuery({
    queryKey: ['stock-regions'],
    queryFn: listStockRegions,
    enabled,
    staleTime: 300_000,
    retry: false,
  });
}

export interface ImspSite {
  id: string;
  name: string;
  registrarIdno: string;
  inactive: boolean;
  // The id /stock/regions lists (CF-35); empty for a site with no place in the catalogue.
  regionId: string;
}

// caiet UC91-13 / CF-23 — "filtrul de locații e limitat la locațiile IMSP". The
// Observer's «Locație» options cannot come from the reports register either, so
// they are the sites the catalogue holds for the IMSP Registrars.
export async function listImspSites(): Promise<ImspSite[]> {
  const { data } = await apiClient.get<{ sites: ImspSite[] }>('/stock/imsp-sites');
  return data.sites ?? [];
}

export function useImspSites(enabled: boolean): UseQueryResult<ImspSite[]> {
  return useQuery({
    queryKey: ['stock-imsp-sites'],
    queryFn: listImspSites,
    enabled,
    staleTime: 300_000,
    retry: false,
  });
}

// UC91-15: a region and a Registrar narrow the locations, as they do in the
// register's facets.
export function imspSiteOptions(
  sites: ImspSite[],
  selection: { registrarIdnos: string[]; regionIds: string[] },
): ScopeOption[] {
  return sites
    .filter(
      (site) =>
        matches(selection.registrarIdnos, site.registrarIdno) &&
        matches(selection.regionIds, site.regionId),
    )
    .map((site) => {
      const option: ScopeOption = { value: site.id, label: site.name };
      if (site.inactive) option.inactive = true;
      return option;
    })
    .sort((a, b) => a.label.localeCompare(b.label, 'ro'));
}

export interface ScopeSelection {
  regionIds: string[];
  localityIds: string[];
  registrarIdnos: string[];
}

export interface ScopeOptionLists {
  regions: ScopeOption[];
  localities: ScopeOption[];
  registrars: ScopeOption[];
  sites: ScopeOption[];
}

function distinct(pairs: [string, string][]): ScopeOption[] {
  const seen = new Map<string, string>();
  for (const [value, label] of pairs) {
    if (value.length > 0 && !seen.has(value)) seen.set(value, label);
  }
  return Array.from(seen, ([value, label]) => ({ value, label })).sort((a, b) =>
    a.label.localeCompare(b.label, 'ro'),
  );
}

function distinctSites(facets: SubmissionPlace[]): ScopeOption[] {
  const seen = new Map<string, ScopeOption>();
  for (const facet of facets) {
    if (facet.siteId.length === 0 || seen.has(facet.siteId)) continue;
    const option: ScopeOption = { value: facet.siteId, label: facet.siteName };
    if (facet.siteActive === false) option.inactive = true;
    seen.set(facet.siteId, option);
  }
  return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label, 'ro'));
}

function matches(values: string[], candidate: string): boolean {
  return values.length === 0 || values.includes(candidate);
}

// UC91-15: a region narrows localities and sites; a Registrar narrows sites.
// Each list is filtered by the selections upstream of it, never by its own.
export function scopeOptions(
  facets: SubmissionPlace[],
  selection: ScopeSelection,
): ScopeOptionLists {
  const inRegion = facets.filter((f) => matches(selection.regionIds, f.regionId));
  const inLocality = inRegion.filter((f) => matches(selection.localityIds, f.localityId));
  return {
    regions: distinct(facets.map((f) => [f.regionId, f.regionName])),
    localities: distinct(inRegion.map((f) => [f.localityId, f.localityName])),
    registrars: distinct(
      inLocality.map((f) => [
        f.registrarIdno,
        registrarFacetLabel(f.registrarName, f.registrarIdno),
      ]),
    ),
    sites: distinctSites(
      inLocality.filter((f) => matches(selection.registrarIdnos, f.registrarIdno)),
    ),
  };
}
